import CustomToast from "components/atoms/CustomToast";
import Button from "components/common/buttons/buttons";
import { FixedButtonContainer } from "components/common/buttons/FixedButtons";
import TopHeader from "components/common/header";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { GroupedSurveyMeta, groupSurveyMeta } from "utils/portfolio/surveyMeta";
import Step1 from "../util/survey/Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Nonmember from "../util/nonmember";
import { useConsumeSurvey } from "utils/clientAPIs/saa";
import { useSession } from "next-auth/react";
import PortfolioResultLoading from "../../Portfolio/ResultLoading";
import { formatAmountManKo, isNumberInvalid } from "../util/result/format";
import {
  ensureStepGuard,
  writeProgress,
  clearProgress,
  resolveUserKey,
  readProgress,
} from "../util/progress";
import Step0 from "../util/survey/Step0";
import { SurveyError } from "../util/Error";
import SaaLoading from "../util/Loading";
import {
  PlannerStepKey,
  usePlannerCompute,
  usePlannerStep,
} from "lib/analytics/react/hooks";
import { trackPageAction } from "lib/analytics/core/actions";

interface SurveyData {
  saving_amount_monthly: number | null;
  saving_total: number | null;
  expense_category: string;
  expected_amount: number | null;
  expected_timing: string;
}

export function getStepKey(step: number): PlannerStepKey {
  switch (step) {
    case 1:
      return "q_spend_amount";
    case 2:
      return "q_spend_timing";
    case 3:
      return "q_savings_accumulated";
    case 4:
      return "q_savings_monthly";
    default:
      return "q_spend_amount";
  }
}

function SaaConsumeIndex({
  surveyList,
  isMobile,
}: {
  surveyList: any;
  isMobile: boolean;
}) {
  // 기본
  const router = useRouter();
  const plannerStep = usePlannerStep();
  const plannerCompute = usePlannerCompute();
  const { data: session }: any = useSession();
  const stepQuery = router.query?.step;
  const subQuery = router.query?.sub;
  const catQuery = router.query?.cat;
  const abQuery = router.query?.ab;
  const currentStep = Array.isArray(stepQuery)
    ? parseInt((stepQuery?.[0] as string) || "1", 10) || 1
    : parseInt(((stepQuery as string) || "1") as string, 10) || 1;

  const gotoStep = useCallback(
    (step: number) => {
      const stepKeyString = getStepKey(step - 1);
      plannerStep.submit({
        step_key: stepKeyString as PlannerStepKey,
        step_index: step,
        outcome: "ok",
      });
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, step: String(step), ab: abQuery },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  // 안정적인 hashId를 1회 생성/재사용
  const nonmemberRef = useRef<Nonmember | null>(null);
  const hashId = session?.user?.email
    ? ""
    : (nonmemberRef.current ??= new Nonmember()).getHashId() || "";

  useEffect(() => {
    if (!router.isReady) return;
    if (!router.query?.step) {
      gotoStep(1);
    }
    // step 가드: 진행 상태 없거나 선행 단계 미완이면 1로 이동
    const userKey = resolveUserKey(session?.user?.email);
    ensureStepGuard("consume", userKey, currentStep, gotoStep);
  }, [
    router.isReady,
    router.query?.step,
    currentStep,
    gotoStep,
    session?.user?.email,
  ]);

  // 저장된 답변 복원 (최초 1회)
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!router.isReady || hydratedRef.current) return;
    const userKey = resolveUserKey(session?.user?.email);
    const prog = readProgress("consume", userKey);
    if (prog?.answers) {
      setSurveyData((prev) => ({
        ...prev,
        ...(prog.answers as Partial<SurveyData>),
      }));
    }
    hydratedRef.current = true;
  }, [router.isReady, session?.user?.email]);

  // 스텝 진입
  useEffect(() => {
    plannerStep.view({
      step_key: getStepKey(currentStep) as PlannerStepKey,
      step_index: currentStep + 1,
    });
  }, [currentStep]);

  // DB 설문 메타 그룹핑
  const grouped: GroupedSurveyMeta = groupSurveyMeta(surveyList);
  // const step2Meta = grouped["expense_category"];
  const step4Meta = grouped["expected_timing"];

  const [surveyData, setSurveyData] = useState<SurveyData>({
    saving_amount_monthly: null,
    saving_total: null,
    expense_category: catQuery as string,
    expected_amount: null,
    expected_timing: step4Meta?.options?.[0]?.answer_id,
  });
  useEffect(() => {
    if (catQuery) {
      const newSurveyData = {
        ...surveyData,
        expense_category: catQuery as string,
        expected_amount: null,
        expected_timing: step4Meta?.options?.[0]?.answer_id,
        saving_amount_monthly: null,
        saving_total: null,
      };
      setSurveyData(newSurveyData);
      writeProgress("consume", resolveUserKey(session?.user?.email), {
        completedStep: currentStep,
        answers: newSurveyData as unknown as Record<string, unknown>,
      });
    }
  }, [catQuery]);

  // 서베이 적재
  const {
    mutate,
    isPending,
    isError,
    isPendingCalc,
    isErrorCalc,
    isPendingResultSave,
    isErrorResultSave,
    isGreatEnough,
    isTooHigh,
    isTimeout,
    recommandTargetAmount,
  } = useConsumeSurvey({ plannerCompute });
  console.log("isErrorCalc", isErrorCalc);
  console.log("isPending", isPending);
  const [isErrorToast, setIsErrorToast] = useState(false);
  useEffect(() => {
    if (
      !isTimeout &&
      !isGreatEnough &&
      !isTooHigh &&
      (isError || isErrorResultSave || isErrorCalc)
    ) {
      Sentry.captureException("SAA Consume 서베이 적재 실패", {
        extra: {
          surveyData,
          isError,
          isErrorResultSave,
          isErrorCalc,
        },
      });
      setIsErrorToast(true);
    }
  }, [
    isError,
    isErrorResultSave,
    isErrorCalc,
    isGreatEnough,
    isTooHigh,
    isTimeout,
  ]);

  const handleNext = useCallback(() => {
    if (currentStep < 4) {
      const userKey = resolveUserKey(session?.user?.email);
      writeProgress("consume", userKey, {
        completedStep: currentStep,
        answers: surveyData as unknown as Record<string, unknown>,
      });
      gotoStep(currentStep + 1);
    }
  }, [currentStep, gotoStep, session?.user?.email, surveyData]);

  const handleBack = useCallback(() => {
    if (subQuery) {
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, sub: undefined },
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    if (currentStep > 1) {
      gotoStep(currentStep - 1);
    } else {
      router.back();
    }
  }, [currentStep, gotoStep, router]);

  const handleMonthSavings = useCallback((option: number) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, saving_amount_monthly: option }));
  }, []);
  const handleTotalSavings = useCallback((option: number) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, saving_total: option }));
  }, []);
  const handleStep2 = useCallback((option: string) => {
    setSurveyData((prev) => ({ ...prev, expense_category: option }));
  }, []);

  const handleStep3 = useCallback((option: number | null) => {
    trackPageAction("input_planner_survey");
    setSurveyData((prev) => ({ ...prev, expected_amount: option }));
  }, []);

  const handleStep4 = useCallback((option: string) => {
    trackPageAction("select_planner_survey");
    setSurveyData((prev) => ({ ...prev, expected_timing: option }));
  }, []);

  const handleComplete = useCallback(
    (recommandTargetAmount?: number) => {
      if (!recommandTargetAmount) {
        plannerStep.submit({
          step_key: "q_savings_monthly",
          step_index: 5,
          outcome: "ok",
        });
      }

      // TODO: 설문 데이터를 서버로 전송하고 결과 페이지로 이동
      const stableHashId = hashId;

      const newAnswer = recommandTargetAmount
        ? {
            ...surveyData,
            expected_amount: Math.floor(recommandTargetAmount / 10000),
          }
        : surveyData;
      setSurveyData(newAnswer);
      mutate({
        answer: newAnswer,
        hashId: stableHashId,
        type: "consume",
        email: session?.user?.email || "",
        isRecommend: recommandTargetAmount ? true : false,
      });
      // 완료 시 진행상태 정리
      const userKey = resolveUserKey(session?.user?.email);
      clearProgress("consume", userKey);
    },
    [surveyData, session, recommandTargetAmount, hashId]
  );

  // 유효성 검사

  const isInvalidStep0 = isNumberInvalid(surveyData.saving_total);
  const isInvalidStep1 = isNumberInvalid(surveyData.saving_amount_monthly);
  const isInvalidStep3 = isNumberInvalid(surveyData.expected_amount);
  const isInvalidStep4 = !surveyData.expected_timing;

  const isAnyPending = isPending || isPendingCalc || isPendingResultSave;
  const isDisabledByStep =
    currentStep === 1
      ? isInvalidStep3
      : currentStep === 2
      ? isInvalidStep4
      : currentStep === 3
      ? isInvalidStep0
      : currentStep === 4
      ? isInvalidStep1
      : true;
  const isDisabled =
    isAnyPending || (currentStep === 1 && !!subQuery) || isDisabledByStep;

  const renderCurrentStep = () => {
    switch (currentStep) {
      // case 1:
      //   return (
      //     <Step1
      //       isMobile={isMobile}
      //       options={{
      //         saving_amount_monthly: surveyData.saving_amount_monthly,
      //         saving_total: surveyData.saving_total,
      //       }}
      //       handleMonthSavings={handleMonthSavings}
      //       handleTotalSavings={handleTotalSavings}
      //     />
      //   );
      case 1:
        return (
          <Step3
            value={surveyData.expected_amount}
            handleValue={handleStep3}
            expenseCategory={surveyData.expense_category}
          />
        );
      case 2:
        return (
          <Step4
            selectedOption={surveyData.expected_timing}
            onOptionSelect={handleStep4}
            expenseCategory={surveyData.expense_category}
            options={step4Meta?.options?.map((o) => ({
              id: o.answer_id,
              label: o.answer,
            }))}
          />
        );
      case 3:
        return (
          <Step0
            saving_total={surveyData.saving_total}
            handleTotalSavings={handleTotalSavings}
            expenseCategory={surveyData.expense_category}
            type="consume"
          />
        );
      case 4:
        return (
          <Step1
            saving_amount_monthly={surveyData.saving_amount_monthly}
            handleMonthSavings={handleMonthSavings}
            expenseCategory={surveyData.expense_category}
            type="consume"
          />
        );
      default:
        return <div></div>;
    }
  };
  //

  if (isPendingCalc || isPending || isPendingResultSave) {
    return (
      <SaaLoading isMobile={isMobile} surveyData={surveyData} type="consume" />
    );
  }

  if (
    isGreatEnough ||
    isTooHigh ||
    isError ||
    isErrorResultSave ||
    isErrorCalc ||
    isTimeout
  ) {
    return (
      <SurveyError
        status={
          isTooHigh
            ? "tooHigh"
            : isGreatEnough
            ? "greatEnough"
            : isTimeout
            ? "timeout"
            : "error"
        }
        isMobile={isMobile}
        surveyData={surveyData}
        handleBack={handleBack}
        type="consume"
        recommandTargetAmount={recommandTargetAmount}
        recommandHandler={handleComplete}
        hashId={hashId || undefined}
        retryHandler={() => {
          mutate({
            answer: surveyData,
            hashId: hashId,
            type: "consume",
            email: session?.user?.email || "",
          });
        }}
        plannerStep={plannerStep}
      />
    );
  }

  return (
    <div
      className="w-full "
      style={{
        height: isMobile ? "100dvh" : "calc(100vh - 80px)",
      }}
    >
      {/* Top Bar */}
      {
        <>
          <TopHeader clickHandler={handleBack} />
          {renderCurrentStep()}
        </>
      }
      {/* Bottom Button */}
      <FixedButtonContainer
        isMobile={isMobile}
        customStyle={{
          background: "transparent",
        }}
      >
        <div className="relative">
          <Button
            size="lg"
            text={"다음"}
            clickHandler={
              isPending
                ? null
                : currentStep === 1 && !!subQuery
                ? null
                : currentStep < 4
                ? handleNext
                : handleComplete
            }
            type="primary"
            btnClass="w-full relative"
            dataGtmId={
              currentStep === 1
                ? "saa-survey-step1-btn"
                : currentStep === 2
                ? "saa-survey-step2-btn"
                : currentStep === 3
                ? "saa-survey-step3-btn"
                : currentStep === 4
                ? "saa-survey-step4-btn"
                : ""
            }
            dataTestId="saa-consume-survey-next-btn"
            disabled={isDisabled}
          />
        </div>
      </FixedButtonContainer>

      <CustomToast
        message="서버 오류가 발생했어요"
        isVisible={isErrorToast}
        onClose={() => setIsErrorToast(false)}
        isSuccess={false}
        width={260}
      />
    </div>
  );
}

export default SaaConsumeIndex;
